/**
 * work-item-store.ts
 *
 * Facade bridging the legacy WorkItemStore API to the new
 * WorkItemRepository, WorkItemCommandService, and WorkItemQueryService.
 */

import type {
  WorkItem,
  WorkItemPatch,
  UpdateWorkItemCommand,
  WorkItemActivity,
  WorkItemRelation,
  WorkItemRelationType,
  WorkItemType,
  WorkItemPriority,
} from './work-item-contracts.js';
import { WorkItemRepository, globalWorkItemRepository } from './work-item-repository.js';
import { WorkItemCommandService, globalWorkItemCommandService, type ConflictError } from './work-item-command-service.js';
import { WorkItemQueryService, globalWorkItemQueryService } from './work-item-query-service.js';

export type { WorkItem, WorkItemActivity, ConflictError };

export interface WorkItemDraft {
  readonly workspaceId: string;
  readonly type: WorkItemType;
  readonly title: string;
  readonly statusId: string;
  readonly createdBy: string;
  readonly priority?: WorkItemPriority;
  readonly assigneeIds?: readonly string[];
  readonly dueAt?: string;
  readonly scheduledAt?: string;
  readonly description?: string;
  readonly labels?: readonly string[];
}

export interface WorkItemExportPayload {
  version: '1.0';
  workspaceId: string;
  exportedAt: string;
  itemCount: number;
  items: WorkItem[];
}

export interface TaskConflict {
  taskId: string;
  localItem: WorkItem;
  importedItem: WorkItem;
  conflictType: 'diverged_content' | 'diverged_status' | 'diverged_both';
}

export interface ImportAnalysisResult {
  totalImported: number;
  newItems: WorkItem[];
  identicalDuplicates: WorkItem[];
  autoUpdatedLatest: WorkItem[];
  conflicts: TaskConflict[];
}

export type ConflictResolutionAction = 'keep_local' | 'use_imported' | 'keep_both';

export interface TaskConflictResolution {
  taskId: string;
  action: ConflictResolutionAction;
}

export class WorkItemStore {
  constructor(
    private readonly repo: WorkItemRepository = globalWorkItemRepository,
    private readonly commandService: WorkItemCommandService = globalWorkItemCommandService,
    private readonly queryService: WorkItemQueryService = globalWorkItemQueryService
  ) {}

  createWorkItem(draft: WorkItemDraft): WorkItem {
    const res = this.commandService.createWorkItem(draft, draft.createdBy);
    if ('kind' in res) {
      throw new Error(`Failed to create work item: ${res.kind === 'validation' ? res.reason : 'Conflict'}`);
    }
    return res;
  }

  updateWorkItem(cmd: UpdateWorkItemCommand, actorId: string): WorkItem | ConflictError {
    const res = this.commandService.updateWorkItem(
      {
        workItemId: cmd.workItemId,
        expectedRevision: cmd.expectedRevision,
        patch: cmd.patch,
        idempotencyKey: cmd.idempotencyKey,
      },
      actorId
    );
    if ('kind' in res && res.kind === 'validation') {
      throw new Error(`Update validation error: ${res.reason}`);
    }
    return res;
  }

  moveWorkItem(workItemId: string, newStatusId: string, actorId: string): WorkItem | ConflictError {
    const res = this.commandService.moveWorkItem(workItemId, newStatusId, actorId);
    if ('kind' in res && res.kind === 'validation') {
      throw new Error(`Move validation error: ${res.reason}`);
    }
    return res;
  }

  addRelation(
    fromWorkItemId: string,
    toWorkItemId: string,
    type: WorkItemRelationType,
    actorId: string
  ): WorkItemRelation | null {
    const res = this.commandService.addRelation(fromWorkItemId, toWorkItemId, type, actorId);
    if ('kind' in res) {
      return null;
    }
    return res;
  }

  archiveWorkItem(workItemId: string, actorId: string): void {
    this.commandService.archiveWorkItem(workItemId, actorId);
  }

  getWorkItems(workspaceId: string, statusId?: string): readonly WorkItem[] {
    return this.queryService.forWorkspace(workspaceId, statusId);
  }

  getWorkItem(id: string): WorkItem | undefined {
    return this.queryService.findById(id);
  }

  getActivities(workItemId: string): readonly WorkItemActivity[] {
    return this.repo.getActivities(workItemId);
  }

  exportWorkItems(workspaceId: string): string {
    const items = this.getWorkItems(workspaceId);
    const payload: WorkItemExportPayload = {
      version: '1.0',
      workspaceId,
      exportedAt: new Date().toISOString(),
      itemCount: items.length,
      items: Array.from(items),
    };
    return JSON.stringify(payload, null, 2);
  }

  analyzeImport(workspaceId: string, rawImportedItems: any[]): ImportAnalysisResult {
    const localItems = this.getWorkItems(workspaceId);
    const localMap = new Map<string, WorkItem>();
    localItems.forEach((i) => localMap.set(i.id, i));

    const newItems: WorkItem[] = [];
    const identicalDuplicates: WorkItem[] = [];
    const autoUpdatedLatest: WorkItem[] = [];
    const conflicts: TaskConflict[] = [];

    for (const raw of rawImportedItems) {
      if (!raw || typeof raw !== 'object' || !raw.id || !raw.title) continue;

      const importedItem: WorkItem = {
        id: String(raw.id),
        workspaceId: raw.workspaceId || workspaceId,
        type: raw.type || 'task',
        title: String(raw.title),
        description: raw.description || '',
        statusId: raw.statusId || raw.status || 'backlog',
        priority: raw.priority || 'normal',
        assigneeIds: Array.isArray(raw.assigneeIds) ? raw.assigneeIds : raw.assignee ? [raw.assignee] : [],
        createdBy: raw.createdBy || 'imported',
        dueAt: raw.dueAt,
        scheduledAt: raw.scheduledAt,
        labels: Array.isArray(raw.labels) ? raw.labels : [],
        relationRefs: Array.isArray(raw.relationRefs) ? raw.relationRefs : [],
        attachmentRefs: Array.isArray(raw.attachmentRefs) ? raw.attachmentRefs : [],
        source: raw.source,
        revision: typeof raw.revision === 'number' ? raw.revision : 1,
        contentHash: raw.contentHash || raw.payloadHash || '',
        payloadHash: raw.payloadHash || raw.contentHash || '',
        eventHash: raw.eventHash,
        createdAt: raw.createdAt || new Date().toISOString(),
        updatedAt: raw.updatedAt || new Date().toISOString(),
        completedAt: raw.completedAt,
        archivedAt: raw.archivedAt,
      };

      const local = localMap.get(importedItem.id);
      if (!local) {
        newItems.push(importedItem);
      } else {
        // Compare hashes and updates
        const localDesc = local.description || '';
        const importDesc = importedItem.description || '';
        const isIdentical =
          local.title === importedItem.title &&
          localDesc === importDesc &&
          local.statusId === importedItem.statusId &&
          local.priority === importedItem.priority &&
          (local.revision || 1) === (importedItem.revision || 1);

        if (isIdentical) {
          identicalDuplicates.push(importedItem);
        } else {
          const localTime = new Date(local.updatedAt).getTime() || 0;
          const importTime = new Date(importedItem.updatedAt).getTime() || 0;
          const localRev = local.revision || 1;
          const importRev = importedItem.revision || 1;

          // If imported item is strictly newer in revision & timestamp and local has not diverged
          if (importRev > localRev && importTime > localTime) {
            autoUpdatedLatest.push(importedItem);
          } else if (localRev > importRev && localTime >= importTime) {
            // Local is strictly newer, skip silently or consider resolved
            identicalDuplicates.push(importedItem);
          } else {
            // Conflict: Divergent changes (e.g. same timestamp/revision but different title/status, or both updated)
            let conflictType: TaskConflict['conflictType'] = 'diverged_content';
            if (local.statusId !== importedItem.statusId && (local.title !== importedItem.title || local.description !== importedItem.description)) {
              conflictType = 'diverged_both';
            } else if (local.statusId !== importedItem.statusId) {
              conflictType = 'diverged_status';
            }
            conflicts.push({
              taskId: importedItem.id,
              localItem: local,
              importedItem,
              conflictType,
            });
          }
        }
      }
    }

    return {
      totalImported: rawImportedItems.length,
      newItems,
      identicalDuplicates,
      autoUpdatedLatest,
      conflicts,
    };
  }

  applyImport(
    workspaceId: string,
    analysis: ImportAnalysisResult,
    resolutions: Record<string, ConflictResolutionAction> = {},
    actorId: string = 'user'
  ): { created: number; updated: number; cloned: number; skipped: number } {
    let created = 0;
    let updated = 0;
    let cloned = 0;
    let skipped = 0;

    // 1. Save new items
    for (const item of analysis.newItems) {
      this.repo.save({ ...item, workspaceId });
      this.repo.recordActivity(item.id, actorId, 'created', `Imported task "${item.title}"`);
      created++;
    }

    // 2. Auto-update strictly latest items
    for (const item of analysis.autoUpdatedLatest) {
      this.repo.save({ ...item, workspaceId });
      this.repo.recordActivity(item.id, actorId, 'updated', `Auto-updated task "${item.title}" from latest import`);
      updated++;
    }

    // 3. Identical duplicates
    skipped += analysis.identicalDuplicates.length;

    // 4. Resolve conflicts
    for (const conflict of analysis.conflicts) {
      const action = resolutions[conflict.taskId] || 'keep_local';

      if (action === 'keep_local') {
        skipped++;
      } else if (action === 'use_imported') {
        const itemToSave: WorkItem = {
          ...conflict.importedItem,
          workspaceId,
          revision: Math.max(conflict.localItem.revision, conflict.importedItem.revision) + 1,
          updatedAt: new Date().toISOString(),
        };
        this.repo.save(itemToSave);
        this.repo.recordActivity(conflict.taskId, actorId, 'updated', `Resolved conflict: Applied imported version of "${itemToSave.title}"`);
        updated++;
      } else if (action === 'keep_both') {
        const timestamp = Date.now();
        const cloneId = `${conflict.importedItem.id}-v2-${timestamp.toString(36)}`;
        const clonedItem: WorkItem = {
          ...conflict.importedItem,
          id: cloneId,
          workspaceId,
          title: `${conflict.importedItem.title} (Imported Version)`,
          revision: 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        this.repo.save(clonedItem);
        this.repo.recordActivity(cloneId, actorId, 'created', `Resolved conflict: Created versioned clone of "${conflict.importedItem.title}"`);
        cloned++;
      }
    }

    return { created, updated, cloned, skipped };
  }
}

export const globalWorkItemStore = new WorkItemStore();
